/* eslint-disable no-console */
import '@ghq-abi/design-system-v2/css';
import 'core-js/actual';

import { ThemeProvider } from '@ghq-abi/design-system-v2';
import { AuthClientProvider } from '@ghq-abi/auth-client-lib/client';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FormatIcu } from '@tolgee/format-icu';
import {
  BackendFetch,
  DevTools,
  Tolgee,
  TolgeeProvider,
  useTolgeeSSR,
} from '@tolgee/react';
import {
  AbstractIntlMessages,
  Locale,
  NextIntlClientProvider,
} from 'next-intl';
import type { AppProps } from 'next/app';
import NextNProgress from 'nextjs-progressbar';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { ErrorBoundary } from '~/app/components';
import { AbilityProvider } from '~/app/contexts/AbilityContext';
import { AuthProvider } from '~/app/contexts/AuthContext';
import { FlagsProvider } from '~/app/contexts/FlagsContext';
import { FullscreenProvider } from '~/app/contexts/FullscreenContext';
import { NavProvider } from '~/app/contexts/NavContext';
import { ProfilePictureProvider } from '~/app/contexts/ProfilePictureContext';
import { ToastProvider } from '~/app/contexts/ToastContext';
import MixPanelProvider from '~/app/contexts/MixPanelContext';
import enUS from '~/app/i18n/en-US.json';
import { globalStyles } from '~/app/styles/global';
import { AppTemplate } from '~/app/templates/App';
import { AppAbility, createUserAbility } from '~/shared/auth/permissions';
import { DEFAULT_LANGUAGE } from '~/shared/constants/i18n';
import { GA, queryClientConfig, useDatadog } from '~/shared/lib';

import { theme } from '@ghq-abi/design-system';
import {
  getPersistedLanguage,
  persistLanguage,
} from '~/shared/utils/languagePersistence';
import '../app/globals.css';
import { NPS } from '~/shared/components/Metrics';
import { CES } from '~/shared/components/MetricsCes';

globalStyles();

type CustomAppProps = {
  Component: AppProps['Component'] & { unauth?: boolean };
  pageProps: AppProps['pageProps'] & {
    session: unknown;
    messages?: AbstractIntlMessages;
    now?: number;
    dehydratedState?: unknown;
    abilityRules?: AppAbility['_indexedRules'];
  };
};

const tolgee = Tolgee()
  .use(BackendFetch())
  .use(DevTools())
  .use(FormatIcu())
  .init({
    defaultLanguage: DEFAULT_LANGUAGE,
    staticData: {
      'en-US': enUS,
    },
    apiKey: process.env.NEXT_PUBLIC_TOLGEE_API_KEY,
    apiUrl: process.env.NEXT_PUBLIC_TOLGEE_API_URL,
  });

tolgee.on('language', e => {
  if (typeof window !== 'undefined') {
    persistLanguage(e.value);
  }
});

export default function App({
  Component,
  pageProps: {
    session,
    messages,
    now,
    abilityRules,
    language,
    flags,
    userNonce,
    ...pageProps
  },
}: CustomAppProps) {
  // https://tanstack.com/query/v4/docs/react/guides/ssr#using-hydration
  const [queryClient] = React.useState(
    () => new QueryClient(queryClientConfig)
  );

  const [clientLanguage, setClientLanguage] = React.useState<Locale>(
    language ?? DEFAULT_LANGUAGE
  );

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const persistedLanguage = getPersistedLanguage();
      if (persistedLanguage !== clientLanguage) {
        setClientLanguage(persistedLanguage);
        tolgee.changeLanguage(persistedLanguage);
      }
    }
  }, [clientLanguage]);

  const locale = clientLanguage;

  useDatadog(locale, session);
  const ssrTolgee = useTolgeeSSR(tolgee, locale);

  return (
    <TolgeeProvider tolgee={ssrTolgee}>
      <ErrorBoundary>
        <NextIntlClientProvider
          messages={messages ?? {}}
          now={now ? new Date(now) : undefined}
          locale={locale}
        >
          <AuthClientProvider session={session}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider>
                {Component.unauth ? (
                  <Component {...pageProps} />
                ) : (
                  <AbilityProvider
                    ability={createUserAbility(undefined, abilityRules)}
                  >
                    <FullscreenProvider
                      startState={pageProps.startFullscreenState}
                    >
                      <FlagsProvider flags={flags}>
                        <AuthProvider>
                          <NavProvider>
                            <ProfilePictureProvider>
                              <MixPanelProvider>
                                <ToastProvider>
                                  <Hydrate state={pageProps.dehydratedState}>
                                    <AppTemplate
                                      // Instead of checking `!!userNonce` it was necessary to put this other check to allow 404 pages to load
                                      hasUserNonce={userNonce !== '[EXPIRED]'}
                                    >
                                      <DndProvider backend={HTML5Backend}>
                                        <Component {...pageProps} />
                                      </DndProvider>
                                    </AppTemplate>
                                  </Hydrate>
                                </ToastProvider>
                              </MixPanelProvider>
                              <NextNProgress
                                color={theme.colors.black.toString()}
                                startPosition={0.3}
                                stopDelayMs={200}
                                height={3}
                              />
                            </ProfilePictureProvider>
                          </NavProvider>
                        </AuthProvider>
                      </FlagsProvider>
                    </FullscreenProvider>
                  </AbilityProvider>
                )}
              </ThemeProvider>

              <GA.Script session={session} />
              <ReactQueryDevtools />
            </QueryClientProvider>
          </AuthClientProvider>
          <NPS.Script
            globalId={session ? session.user.gid : null}
            preferredLanguage={locale}
          />
          <CES.Script
            globalId={session ? session.user.gid : null}
            preferredLanguage={locale}
          />
        </NextIntlClientProvider>
      </ErrorBoundary>
    </TolgeeProvider>
  );
}
